import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { keys } from "../api/queryKeys.ts";
import { api } from "../api/client.ts";
import CountryName from "../components/CountryName";
import DetailRow from "../components/DetailRow.tsx";
import FormattedDate from "../components/FormattedDate";
import SectionCard from "../components/SectionCard.tsx";
import StatusChip from "../components/StatusChip.tsx";
import DetailHeader from "../components/detail/DetailHeader.tsx";
import DetailPageStatus from "../components/detail/DetailPageStatus.tsx";
import MatchedCompanyCard from "../components/detail/MatchedCompanyCard.tsx";
import SourceDataCard from "../components/detail/SourceDataCard.tsx";
import { ensureHttps } from "../utils/url.ts";

const BACK = { to: "/regulatory-filings", label: "Regulatory filings" };

function RegulatoryFilingDetailPage() {
  const { id } = useParams<{ id: string }>();

  const { data: filing, isLoading, isError } = useQuery({
    queryKey: keys.regulatoryFiling.detail(id),
    queryFn: () => api.getRegulatoryFiling(id!),
    enabled: !!id,
  });

  if (isLoading || isError || !filing) {
    return <DetailPageStatus isLoading={isLoading} message="Regulatory filing not found" back={BACK} />;
  }

  return (
    <>
      <DetailHeader
        back={BACK}
        title={filing.title}
        sourceUrl={ensureHttps(filing.source_url)}
        chips={
          <>
            <Chip size="small" label={filing.source} />
            <StatusChip
              kind="regulatory_filing"
              value={filing.status}
              variant={filing.is_active ? "filled" : "outlined"}
            />
            {filing.filing_type && <Chip size="small" label={filing.filing_type} variant="outlined" />}
            {filing.filing_subtype && <Chip size="small" label={filing.filing_subtype} variant="outlined" />}
            {filing.country && (
              <Chip size="small" label={<CountryName code={filing.country} />} variant="outlined" />
            )}
          </>
        }
      />

      <SectionCard title="Details">
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <DetailRow label="Applicant" value={filing.applicant_name} />
            <DetailRow label="Filing type" value={filing.filing_type} />
            <DetailRow label="Filing subtype" value={filing.filing_subtype} />
            <DetailRow label="Status" value={filing.status} />
            <DetailRow label="Status (raw)" value={filing.status_raw} />
            <DetailRow label="Active" value={filing.is_active ? "Yes" : "No"} />
            <DetailRow label="External ID" value={filing.external_id} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <DetailRow
              label="Filed at"
              value={filing.filed_at ? <FormattedDate value={filing.filed_at} /> : null}
            />
            <DetailRow
              label="Decision at"
              value={filing.decision_at ? <FormattedDate value={filing.decision_at} /> : null}
            />
            <DetailRow
              label="Hearing deadline"
              value={filing.hearing_deadline ? <FormattedDate value={filing.hearing_deadline} /> : null}
            />
            <DetailRow label="Region" value={filing.region} />
            <DetailRow label="Subregion" value={filing.subregion} />
            <DetailRow
              label="Capacity"
              value={filing.capacity_mw !== null ? `${filing.capacity_mw} MW` : null}
            />
            <DetailRow
              label="Estimated production"
              value={
                filing.estimated_production_gwh !== null
                  ? `${filing.estimated_production_gwh} GWh`
                  : null
              }
            />
            <DetailRow label="Created" value={<FormattedDate value={filing.created_at} />} />
            <DetailRow label="Updated" value={<FormattedDate value={filing.updated_at} />} />
          </Grid>
        </Grid>
      </SectionCard>

      {filing.master_company_id && (
        <MatchedCompanyCard
          companyId={filing.master_company_id}
          name={filing.master_company_name ?? filing.applicant_name}
          method={filing.match_method}
          confidence={filing.match_confidence}
        />
      )}

      <SourceDataCard data={filing.source_data} />
    </>
  );
}

export default RegulatoryFilingDetailPage;
